const LOOKUP_TIMEOUT_MS = 6000;

const VPN_NAME_RE = /\b(vpn|nordvpn|expressvpn|mullvad|proton|surfshark|cyberghost|windscribe|tunnelbear|ipvanish|purevpn|vyprvpn|hide\.?me|hotspot\s*shield|private\s*internet|urban\s*vpn|cloudflare\s*warp|outline)\b/i;

const HOSTING_NAME_RE = /\b(hetzner|digitalocean|linode|ovh|vultr|m247|datacamp|leaseweb|choopa|psychz|clouvider|serverion|colocrossing|hostinger|contabo)\b/i;

const RU_TIMEZONES = new Set([
    'Europe/Kaliningrad',
    'Europe/Moscow',
    'Europe/Simferopol',
    'Europe/Kirov',
    'Europe/Volgograd',
    'Europe/Astrakhan',
    'Europe/Saratov',
    'Europe/Ulyanovsk',
    'Europe/Samara',
    'Asia/Yekaterinburg',
    'Asia/Omsk',
    'Asia/Novosibirsk',
    'Asia/Barnaul',
    'Asia/Tomsk',
    'Asia/Novokuznetsk',
    'Asia/Krasnoyarsk',
    'Asia/Irkutsk',
    'Asia/Chita',
    'Asia/Yakutsk',
    'Asia/Khandyga',
    'Asia/Vladivostok',
    'Asia/Ust-Nera',
    'Asia/Magadan',
    'Asia/Sakhalin',
    'Asia/Srednekolymsk',
    'Asia/Kamchatka',
    'Asia/Anadyr',
]);

function getBrowserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
}

function normalizeCountry(code) {
    return String(code || '').trim().toUpperCase();
}

export function isLikelyVpn(data, timeZone = getBrowserTimeZone()) {
    if (!data || typeof data !== 'object' || data.error) return false;
    if (data.is_vpn || data.is_proxy || data.is_tor) return true;

    const org = `${data.company_name || ''} ${data.asn_org || ''} ${data.org || ''}`;
    if (VPN_NAME_RE.test(org)) return true;

    const countryCode = normalizeCountry(data.countryCode || data.cc);
    const abroad = Boolean(countryCode) && countryCode !== 'RU';

    if (abroad && RU_TIMEZONES.has(timeZone)) return true;
    if (abroad && (data.is_datacenter || HOSTING_NAME_RE.test(org))) return true;

    return false;
}

function mergeSignals(parts) {
    const merged = {
        is_vpn: false,
        is_proxy: false,
        is_tor: false,
        is_datacenter: false,
        countryCode: '',
        company_name: '',
        asn_org: '',
        org: '',
    };

    parts.filter(Boolean).forEach((part) => {
        merged.is_vpn = merged.is_vpn || Boolean(part.is_vpn);
        merged.is_proxy = merged.is_proxy || Boolean(part.is_proxy);
        merged.is_tor = merged.is_tor || Boolean(part.is_tor);
        merged.is_datacenter = merged.is_datacenter || Boolean(part.is_datacenter);
        if (!merged.countryCode && (part.countryCode || part.cc)) {
            merged.countryCode = normalizeCountry(part.countryCode || part.cc);
        }
        merged.company_name = [merged.company_name, part.company_name].filter(Boolean).join(' ');
        merged.asn_org = [merged.asn_org, part.asn_org].filter(Boolean).join(' ');
        merged.org = [merged.org, part.org].filter(Boolean).join(' ');
    });

    return merged;
}

async function fetchJson(url, signal) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

    if (signal) {
        if (signal.aborted) {
            clearTimeout(timer);
            throw new DOMException('Aborted', 'AbortError');
        }
        signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

async function lookupIpapiIs(signal) {
    try {
        const data = await fetchJson('https://api.ipapi.is', signal);
        if (!data || data.error) return null;
        return {
            is_vpn: data.is_vpn,
            is_proxy: data.is_proxy,
            is_tor: data.is_tor,
            is_datacenter: data.is_datacenter,
            countryCode: data.cc,
            company_name: data.company_name,
            asn_org: data.asn_org,
        };
    } catch {
        return null;
    }
}

async function lookupIpwho(signal) {
    try {
        const data = await fetchJson('https://ipwho.is/', signal);
        if (!data || data.success === false) return null;
        return {
            countryCode: data.country_code,
            org: `${data.connection?.org || ''} ${data.connection?.isp || ''}`,
        };
    } catch {
        return null;
    }
}

async function lookupGeoJs(signal) {
    try {
        const data = await fetchJson('https://get.geojs.io/v1/ip/geo.json', signal);
        if (!data) return null;
        return {
            countryCode: data.country_code,
            org: `${data.organization || ''} ${data.organization_name || ''}`,
        };
    } catch {
        return null;
    }
}

export async function checkVpnEnabled(signal) {
    const parts = await Promise.all([
        lookupIpapiIs(signal),
        lookupIpwho(signal),
        lookupGeoJs(signal),
    ]);

    return isLikelyVpn(mergeSignals(parts));
}
